import { IGatsbyImageData } from "gatsby-plugin-image"
import { resolve } from "path"
import slugify from "slugify"
import { translationCountries } from "../constants/countries"
import flattenQueryResult from "../helpers/flatten-query-result"
import { GatsbyImage } from "../types"
import { GatsbyCreatePages } from "./blog.interface"

export interface PhoneData {
  name: string
  model: string
  headOver10g: number
  headOver1g: number
  bodyOver10g: number
  bodyOver1g: number
  sarSourceLink: string
  imageSourceLink: string
  image?: { gatsbyImageData: IGatsbyImageData }
  logoImage?: { gatsbyImageData: IGatsbyImageData }
}

export const createSarCheckerPhoneSlug = ({
  name,
  model,
}: {
  name: string
  model: string
}) =>
  slugify(`${name}-${model}`, {
    remove: /"<>#%\{\}\|\\\^~\[\]`;\?:@=&/g,
  }).toLowerCase()

interface GraphQLQueryParams {
  graphql: any
  context?: Record<string, any>
}

const getData = async ({
  graphql,
  context,
}: GraphQLQueryParams): Promise<{
  phoneDataList: PhoneData[]
  placeholderImage: GatsbyImage
}> => {
  const result = await graphql(
    `
      query PhoneDataQuery($locale: String!) {
        allContentfulSarChceckerPhone(
          filter: { node_locale: { eq: $locale } }
        ) {
          edges {
            node {
              name
              model
              headOver10g
              headOver1g
              bodyOver10g
              bodyOver1g
              sarSourceLink
              imageSourceLink
              image {
                gatsbyImageData(
                  layout: CONSTRAINED
                  placeholder: BLURRED
                  quality: 100
                  width: 560
                )
              }
            }
          }
        }
        placeholder: file(relativePath: { eq: "phone-placeholder.png" }) {
          image: childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              quality: 100
              width: 560
            )
          }
        }
      }
    `,
    context
  )
  return {
    phoneDataList: flattenQueryResult<PhoneData>(
      result.data.allContentfulSarChceckerPhone
    ),
    placeholderImage: result.data.placeholder.gatsbyImageData,
  }
}

const createSarCheckerPhone: GatsbyCreatePages = async ({
  graphql,
  actions: { createPage },
}) => {
  const sarCheckerPhoneTemplate = resolve(
    `./src/templates/sar-checker-phone-template.tsx`
  )

  await Promise.all(
    translationCountries.map(async ({ locale, pathPrefix }) => {
      const { phoneDataList, placeholderImage } = await getData({
        graphql,
        context: { locale },
      })

      const mapPhoneDataImage = (phoneData: PhoneData) => {
        if (!phoneData.image) {
          phoneData.image = placeholderImage
          phoneData.imageSourceLink = ""
        }

        return phoneData
      }

      phoneDataList.map(mapPhoneDataImage).forEach((phoneData) => {
        const slug = createSarCheckerPhoneSlug(phoneData)

        createPage({
          path: `${pathPrefix}/community/sar-checker/${slug}/`,
          component: sarCheckerPhoneTemplate,
          context: { phoneData, locale },
        })
      })
    })
  )
}

export default createSarCheckerPhone
